/**
 * AvailabilitySection.tsx — Módulo Formularios, Etapa 3. Config de servicio
 * reservable + fechas bloqueadas + lista de reservas. Compone primitives
 * existentes (Toggle/Dropdown/Input/Badge/DatePicker) — mismo criterio que
 * FormFieldEditor, sin primitive nuevo.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BookingPricingMode } from '@evo-hub/shared';
import { Toggle } from '../ui/Toggle.js';
import { Checkbox } from '../ui/Checkbox.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Badge } from '../ui/Badge.js';
import { IconButton } from '../ui/IconButton.js';
import { Icon } from '../Icon.js';
import { DatePicker } from '../ui/DatePicker.js';
import { LoadingState } from '../ui/LoadingState.js';
import { DetailSection } from '../ui/DetailSection.js';
import { KVGrid } from '../ui/KVGrid.js';
import { KVRow } from '../ui/KVRow.js';
import { EmptyState } from '../ui/EmptyState.js';
import { SubRow, SubRowList } from '../ui/SubRow.js';
import { useToast } from '../ui/Toast.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { parseServerDate } from '../../lib/date.js';
import {
  useFormService, useUpsertFormService, useRemoveFormService,
  useServiceBlackouts, useAddBlackout, useRemoveBlackout,
  useServiceBookings, useCancelBooking,
  type UpsertServiceInput,
} from '../../hooks/useBookings.js';

const PRICING_MODES: BookingPricingMode[] = ['fixed', 'per_night', 'per_person', 'per_night_and_person'];
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

function toISODate(d: Date): string { return d.toISOString().slice(0, 10); }
function fromISODate(s: string): Date { return new Date(`${s}T00:00:00Z`); }
function fmtCents(cents: number, currency: string): string {
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency.toUpperCase() });
}
function fmtDate(iso: string): string {
  return parseServerDate(iso + 'T00:00:00Z').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Meta en caption para el header de tarjeta (conteos), como en el handoff. */
function HeaderMeta({ children }: { children: ReactNode }) {
  return (
    <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

const DEFAULT_FORM: UpsertServiceInput = {
  pricingMode: 'fixed', basePrice: 0, pricePerPerson: null,
  multiDay: false,
  minNights: null, maxNights: null, minPeople: 1, maxPeople: null,
  capacityPerDate: 1, weeklyAvailability: WEEKDAYS,
};

export function AvailabilitySection({ formId }: { formId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const { data, isLoading } = useFormService(formId);
  const upsert = useUpsertFormService(formId);
  const removeService = useRemoveFormService(formId);
  const service = data?.service ?? null;
  const enabled = !!service;

  const [form, setForm] = useState<UpsertServiceInput>(DEFAULT_FORM);
  useEffect(() => {
    if (service) {
      setForm({
        pricingMode: service.pricingMode, basePrice: service.basePrice, pricePerPerson: service.pricePerPerson,
        multiDay: service.multiDay,
        minNights: service.minNights, maxNights: service.maxNights, minPeople: service.minPeople, maxPeople: service.maxPeople,
        capacityPerDate: service.capacityPerDate, weeklyAvailability: service.weeklyAvailability,
      });
    }
  }, [service]);

  // Cuántos días dura una reserva ya no se deduce del precio: lo dice el
  // servicio. Un pase de día con precio por persona pide una fecha; una
  // estadía de precio fijo pide entrada y salida.
  const isRangeMode = form.multiDay === true;
  // "Este servicio no cobra": el formulario reserva la fecha y no pide plata.
  // No hace falta un campo nuevo — sin precios el público no emite la caja de
  // total (public-forms.ts: `cobra`). La casilla hace explícito lo que antes
  // había que adivinar poniendo 0 a mano en dos campos.
  const cobra = (form.basePrice ?? 0) > 0 || (form.pricePerPerson ?? 0) > 0;

  // El estado se DERIVA del precio, salvo que quien edita haya dicho otra cosa
  // en esta sesión.
  //
  // Antes era `useState(!cobra)`, y useState fija su valor en el PRIMER render
  // — cuando el servicio todavía no cargó y el formulario tiene los ceros por
  // defecto. Con un servicio de $160 la casilla aparecía marcada, escondía los
  // campos de precio, y quien lo miraba se iba convencido de que no cobraba
  // mientras el público seguía mostrando el total. Es lo que pasó con Tiny
  // Homes de Villa Sion.
  //
  // `null` = todavía nadie tocó la casilla, así que manda el dato. Hace falta
  // el estado igual: al desmarcarla los precios quedan en cero, y sin esto se
  // volvería a marcar sola antes de poder escribir el primero.
  const [sinPrecioElegido, setSinPrecioElegido] = useState<boolean | null>(null);
  const sinPrecio = sinPrecioElegido ?? !cobra;
  const hasPeoplePricing = form.pricingMode === 'per_person' || form.pricingMode === 'per_night_and_person';

  async function handleToggleEnable(v: boolean) {
    if (v) {
      upsert.mutate(DEFAULT_FORM);
      return;
    }
    const ok = await confirm({ title: t('forms.availability.removeConfirm'), tone: 'danger' });
    if (ok) removeService.mutate();
  }

  function handleSave() {
    upsert.mutate(form, { onSuccess: () => toast.info(t('forms.availability.saved')) });
  }

  function toggleWeekday(d: number) {
    setForm(f => {
      const set = new Set(f.weeklyAvailability ?? WEEKDAYS);
      if (set.has(d)) set.delete(d); else set.add(d);
      return { ...f, weeklyAvailability: Array.from(set).sort() };
    });
  }

  const num = (v: string): number | null => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  if (isLoading) return <LoadingState size="sm" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      {dialog}

      {/* Fila 1 (V2): Reservas (estado + precio) | Límites. Tarjetas del
          sistema, nada suelto sobre la página. */}
      <div className="evo-duo-grid">
      <DetailSection
        title={t('forms.availability.bookingsSection')}
        titleRight={<Toggle checked={enabled} onChange={v => { void handleToggleEnable(v); }} label={t('forms.availability.enableLabel')} size="sm" />}
      >
      {!enabled && <EmptyState size="sm" reserveSpace title={t('forms.availability.emptyState')} />}

      {enabled && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)', paddingBottom: 'var(--evo-space-3)' }}>
            <span style={{ paddingTop: 2, flexShrink: 0 }}>
              <Checkbox
                checked={sinPrecio}
                onChange={v => {
                  setSinPrecioElegido(v);
                  // Marcar la casilla deja los precios en cero: es lo que el
                  // formulario público lee para no mostrar el total.
                  if (v) setForm(f => ({ ...f, basePrice: 0, pricePerPerson: null }));
                }}
                size="sm"
              />
            </span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)', color: sinPrecio ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)' }}>
                {t('forms.availability.noPriceLabel')}
              </span>
              <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', lineHeight: 1.5 }}>
                {t('forms.availability.noPriceHint')}
              </span>
            </span>
          </div>

          {!sinPrecio && (
          <KVGrid columns={1}>
            <KVRow
              label={t('forms.availability.pricingModeLabel')}
              value={
                <Dropdown<BookingPricingMode>
                  value={form.pricingMode}
                  options={PRICING_MODES.map(m => ({ value: m, label: t(`forms.availability.pricingMode.${m}`) }))}
                  onChange={m => setForm(f => ({ ...f, pricingMode: m }))}
                  size="sm"
                  align="right"
                />
              }
            />
            <KVRow
              label={t('forms.availability.basePriceLabel')}
              value={<Input type="number" size="sm" min={0} value={String(form.basePrice)} onChange={v => setForm(f => ({ ...f, basePrice: num(v) ?? 0 }))} />}
            />
            {hasPeoplePricing ? (
              <KVRow
                label={t('forms.availability.pricePerPersonLabel')}
                value={<Input type="number" size="sm" min={0} value={String(form.pricePerPerson ?? 0)} onChange={v => setForm(f => ({ ...f, pricePerPerson: num(v) }))} />}
                last
              />
            ) : null}
          </KVGrid>
          )}
          {!sinPrecio && (
          <p style={{ margin: 'var(--evo-space-2) 0 0', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
            {t('forms.availability.priceHint')}
          </p>
          )}
          <div style={{ marginTop: 'var(--evo-space-4)', display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="solid" tone="cyan" size="sm" loading={upsert.isPending} onClick={handleSave}>
              {t('forms.availability.saveBtn')}
            </Button>
          </div>
        </>
      )}
      </DetailSection>

      <DetailSection title={t('forms.availability.limitsSection', { defaultValue: 'Límites' })}>
      {!enabled && <EmptyState size="sm" reserveSpace title={t('forms.availability.emptyState')} />}
      {enabled && (
        <KVGrid columns={1}>
          <KVRow
            label={t('forms.availability.stayModeLabel')}
            value={
              <Dropdown<'single' | 'multi'>
                value={form.multiDay ? 'multi' : 'single'}
                options={[
                  { value: 'single', label: t('forms.availability.stayMode.single') },
                  { value: 'multi', label: t('forms.availability.stayMode.multi') },
                ]}
                onChange={v => setForm(f => ({
                  ...f,
                  multiDay: v === 'multi',
                  // Los topes de noches no significan nada en un pase de día.
                  ...(v === 'single' ? { minNights: null, maxNights: null } : {}),
                }))}
                size="sm"
                align="right"
              />
            }
          />
          {isRangeMode ? (
            <KVRow
              label={t('forms.availability.minNightsLabel')}
              value={<Input type="number" size="sm" min={1} value={form.minNights != null ? String(form.minNights) : ''} onChange={v => setForm(f => ({ ...f, minNights: v ? num(v) : null }))} />}
            />
          ) : null}
          {isRangeMode ? (
            <KVRow
              label={t('forms.availability.maxNightsLabel')}
              value={<Input type="number" size="sm" min={1} value={form.maxNights != null ? String(form.maxNights) : ''} onChange={v => setForm(f => ({ ...f, maxNights: v ? num(v) : null }))} />}
            />
          ) : null}
          <KVRow
            label={t('forms.availability.minPeopleLabel')}
            value={<Input type="number" size="sm" min={1} value={String(form.minPeople ?? 1)} onChange={v => setForm(f => ({ ...f, minPeople: num(v) ?? 1 }))} />}
          />
          <KVRow
            label={t('forms.availability.maxPeopleLabel')}
            value={<Input type="number" size="sm" min={1} value={form.maxPeople != null ? String(form.maxPeople) : ''} onChange={v => setForm(f => ({ ...f, maxPeople: v ? num(v) : null }))} />}
          />
          <KVRow
            label={t('forms.availability.capacityLabel')}
            value={<Input type="number" size="sm" min={1} value={String(form.capacityPerDate ?? 1)} onChange={v => setForm(f => ({ ...f, capacityPerDate: num(v) ?? 1 }))} />}
          />
          <KVRow
            label={t('forms.availability.weekdaysLabel')}
            value={
              <div style={{ display: 'flex', gap: 'var(--evo-space-1)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {WEEKDAYS.map(d => {
                  const active = (form.weeklyAvailability ?? WEEKDAYS).includes(d);
                  return (
                    <Badge key={d} variant={active ? 'solid' : 'soft'} tone={active ? 'cyan' : 'muted'} size="sm" onClick={() => toggleWeekday(d)}>
                      {t(`forms.availability.weekday.${d}`)}
                    </Badge>
                  );
                })}
              </div>
            }
            last
          />
        </KVGrid>
      )}
      </DetailSection>
      </div>

      {enabled && (
        <div className="evo-duo-grid">
          <DetailSection
            title={t('forms.availability.blackoutsSection')}
            titleRight={<BlackoutsCount formId={formId} />}
          >
            <BlackoutsEditor formId={formId} />
          </DetailSection>
          <DetailSection
            title={t('forms.availability.bookingsListSection', { defaultValue: 'Reservas recibidas' })}
            titleRight={<BookingsCount formId={formId} />}
          >
            <BookingsList formId={formId} />
          </DetailSection>
        </div>
      )}
    </div>
  );
}

/* Conteos en el header de la tarjeta, como en el handoff ("2 dominios",
   "21 envíos"). react-query dedupe: misma cache que la lista de abajo. */
function BlackoutsCount({ formId }: { formId: string }) {
  const { t } = useTranslation();
  const { data } = useServiceBlackouts(formId, true);
  const n = data?.blackouts.length ?? 0;
  return n > 0 ? <HeaderMeta>{t('forms.availability.blackoutsCount', { count: n })}</HeaderMeta> : null;
}

function BookingsCount({ formId }: { formId: string }) {
  const { t } = useTranslation();
  const { data } = useServiceBookings(formId, true);
  const n = data?.bookings.length ?? 0;
  return n > 0 ? <HeaderMeta>{t('forms.availability.bookingsCount', { count: n })}</HeaderMeta> : null;
}

function BlackoutsEditor({ formId }: { formId: string }) {
  const { t } = useTranslation();
  const { data } = useServiceBlackouts(formId, true);
  const addBlackout = useAddBlackout(formId);
  const removeBlackout = useRemoveBlackout(formId);
  const [range, setRange] = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const [reason, setReason] = useState('');

  const blackouts = data?.blackouts ?? [];

  function handleAdd() {
    if (!range.from) return;
    const dateStart = toISODate(range.from);
    const dateEnd = toISODate(range.to ?? range.from);
    addBlackout.mutate({ dateStart, dateEnd, ...(reason ? { reason } : {}) }, {
      onSuccess: () => { setRange({ from: null, to: null }); setReason(''); },
    });
  }

  return (
    <div>
      <p style={{ fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', marginBottom: 'var(--evo-space-3)', marginTop: 0 }}>{t('forms.availability.blackoutsHint')}</p>

      {blackouts.length > 0 ? (
        <div style={{ marginBottom: 'var(--evo-space-3)' }}>
          <SubRowList>
            {blackouts.map(b => (
              <SubRow
                key={b.id}
                title={`${fmtDate(b.dateStart)}${b.dateEnd !== b.dateStart ? ` → ${fmtDate(b.dateEnd)}` : ''}`}
                meta={b.reason || undefined}
                right={<IconButton icon={<Icon name="close" size={12} />} ariaLabel={t('common.delete', 'Eliminar')} variant="ghost" tone="danger" size="sm" onClick={() => removeBlackout.mutate(b.id)} />}
              />
            ))}
          </SubRowList>
        </div>
      ) : (
        <p style={{ fontSize: 'var(--evo-fs-label)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', marginBottom: 'var(--evo-space-3)', marginTop: 0 }}>{t('forms.availability.blackoutsEmpty')}</p>
      )}

      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <DatePicker mode="range" value={range} onChange={setRange} size="sm" placeholder={`${t('forms.availability.blackoutFromLabel')} — ${t('forms.availability.blackoutToLabel')}`} />
        <Input type="text" size="sm" value={reason} onChange={setReason} placeholder={t('forms.availability.blackoutReasonPlaceholder')} />
        <Button type="button" variant="outline" tone="muted" size="sm" loading={addBlackout.isPending} disabled={!range.from} onClick={handleAdd}>
          {t('forms.availability.addBlackout')}
        </Button>
      </div>
    </div>
  );
}

function BookingsList({ formId }: { formId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useServiceBookings(formId, true);
  const cancelBooking = useCancelBooking(formId);
  const { confirm, dialog } = useConfirm();
  const bookings = data?.bookings ?? [];

  async function handleCancel(id: string) {
    const ok = await confirm({ title: t('forms.availability.cancelConfirm'), tone: 'danger' });
    if (ok) cancelBooking.mutate(id);
  }

  return (
    <div>
      {dialog}
      {isLoading && <LoadingState size="sm" />}
      {!isLoading && bookings.length === 0 && <EmptyState size="sm" reserveSpace title={t('forms.availability.bookingsEmpty')} />}
      {bookings.length > 0 && (
        <SubRowList>
          {bookings.map(b => (
            <SubRow
              key={b.id}
              title={`${fmtDate(b.dateStart)} → ${fmtDate(b.dateEnd)}`}
              meta={`${b.people} · ${fmtCents(b.priceTotal, b.currency)}`}
              right={
                <>
                  <Badge variant="outline" tone={b.status === 'confirmed' ? 'cyan' : 'muted'} size="sm">
                    {t(`forms.availability.status.${b.status}`)}
                  </Badge>
                  {b.status === 'confirmed' && (
                    <IconButton icon={<Icon name="close" size={12} />} ariaLabel={t('forms.availability.cancelBtn')} variant="ghost" tone="danger" size="sm" onClick={() => { void handleCancel(b.id); }} />
                  )}
                </>
              }
            />
          ))}
        </SubRowList>
      )}
    </div>
  );
}
